import type { ClinicianIdentityProofingRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { useState } from 'react';

import { useMyEmployeeRecordQuery } from '@/entities/me';
import { AlertTriangle } from 'lucide-react';

import {
  formatPhoneInput,
  maskDigitsOnly,
  maskZipInput,
  stripPhone,
} from '@/shared/lib/formatters';
import { commitOrPushErrors } from '@/shared/lib/forms';
import { FormField } from '@/shared/ui/primitives';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { FieldGroup } from '@/shared/ui/shadcn/field';

import { maskDobMdyInput, toDobIsoZ, useDemographicInfoForm } from '../../model/dosespot';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';
import { IdpDemographicsReviewDialog } from './IdpDemographicsReviewDialog';

export interface IdpDemographicInfoStepProps {
  onSubmit: (demographicInfo: ClinicianIdentityProofingRequestT) => void;
  submitError: Error | null;
}

export function IdpDemographicInfoStep(props: IdpDemographicInfoStepProps) {
  const { onSubmit, submitError } = props;
  const employeeRecordQuery = useMyEmployeeRecordQuery();
  const { form, validateDraft } = useDemographicInfoForm({
    employeeRecord: employeeRecordQuery.data,
  });

  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [lastSeenError, setLastSeenError] = useState<Error | null>(submitError);

  if (submitError !== lastSeenError) {
    setLastSeenError(submitError);
    if (submitError) {
      setReviewDialogOpen(true);
    }
  }

  const handleSubmit = () => {
    const validated = commitOrPushErrors(form, validateDraft());
    if (!validated) {
      return;
    }

    const { demographicInfo } = validated;
    onSubmit({
      ...demographicInfo,
      DateOfBirth: toDobIsoZ(demographicInfo.DateOfBirth),
      PhoneNumber: stripPhone(demographicInfo.PhoneNumber),
      IsMobileNumber: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <IdpDemographicsReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
      />

      {submitError ? (
        <Alert variant="destructive">
          <AlertTitle>Submission failed</AlertTitle>
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      <form
        id={DS_STEP_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <FieldGroup>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              form={form}
              type="text"
              name="demographicInfo.FirstName"
              label="Legal first name"
              inputProps={{ autoComplete: 'given-name' }}
            />

            <FormField
              form={form}
              type="text"
              name="demographicInfo.LastName"
              label="Legal last name"
              inputProps={{ autoComplete: 'family-name' }}
            />
          </div>

          <FormField
            form={form}
            type="text"
            name="demographicInfo.DateOfBirth"
            label="Date of birth"
            placeholder="MM/DD/YYYY"
            parse={maskDobMdyInput}
            inputProps={{
              autoComplete: 'bday',
              inputMode: 'numeric',
              maxLength: 10,
            }}
          />

          <FormField
            form={form}
            type="text"
            name="demographicInfo.HomeAddress"
            label="Personal home address"
            inputProps={{ autoComplete: 'street-address' }}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField
              form={form}
              type="text"
              name="demographicInfo.City"
              label="City"
              inputProps={{ autoComplete: 'address-level2' }}
            />

            <FormField
              form={form}
              type="text"
              name="demographicInfo.State"
              label="State"
              inputProps={{ placeholder: 'e.g. CA', autoComplete: 'address-level1' }}
            />

            <FormField
              form={form}
              type="text"
              name="demographicInfo.ZipCode"
              label="ZIP code"
              parse={maskZipInput}
              inputProps={{ autoComplete: 'postal-code', inputMode: 'numeric', maxLength: 10 }}
            />
          </div>

          <FormField
            form={form}
            type="text"
            name="demographicInfo.PhoneNumber"
            label="Personal mobile number"
            placeholder="(555) 123-4567"
            parse={formatPhoneInput}
            inputProps={{ autoComplete: 'tel', inputMode: 'tel' }}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField
              form={form}
              type="password"
              name="demographicInfo.SocialSecurityNumber"
              label="Social Security Number"
              parse={(raw) => maskDigitsOnly(raw, 9)}
              inputProps={{
                autoComplete: 'off',
                inputMode: 'numeric',
                maxLength: 9,
              }}
            />

            <FormField
              form={form}
              type="password"
              name="demographicInfo.CreditCardNumber"
              label="Credit card number"
              parse={(raw) => maskDigitsOnly(raw, 16)}
              inputProps={{
                autoComplete: 'off',
                inputMode: 'numeric',
                maxLength: 16,
              }}
            />
          </div>

          <div className="flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-2.5 text-xs text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>
              You must use a <strong>credit card</strong> — debit cards are not accepted. All
              information on this page must be your personal details, not your office or business
              information.
            </span>
          </div>
        </FieldGroup>
      </form>
    </div>
  );
}
